import { useEffect, useMemo, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import type { CanvasSummary, VideoReference } from '@/api/types';
import { useReflowAnchor } from '@/features/video/useReflowAnchor';
import { useVideoDetail } from '@/features/video/VideoDetailContext';
import { useAddToCanvasPicker } from '@/library/AddToCanvasPicker';
import { useBookmarkMutations } from '@/library/useBookmarks';
import { canvasCountOf, useCanvases } from '@/library/useCanvases';
import { SAVED_TITLE_MAX, savedDisplayTitle, savedTitlePatch } from '@/library/savedTitle';
import { useSavedTitle, useSavedVideos } from '@/library/useSavedVideos';
import { useT, useTranslator } from '@/i18n/LocaleProvider';
import { cx } from '@/lib/cx';
import { formatDuration } from '@/lib/format';
import { useResizeWidth } from '@/lib/useResizeWidth';
import styles from './Library.module.css';

/** Saved: закладки с мультивыбором, «→ Canvas» на карточке, «Add to canvas →» / «Remove» для выбранных. */
export function SavedView() {
  const t = useT();
  const navigate = useNavigate();
  const { data: videos = [], isLoading } = useSavedVideos();
  // Список канвасов нужен, чтобы служебная доска закладок не считалась канвасом.
  const { data: canvasList = [] } = useCanvases();
  const { setMany } = useBookmarkMutations();
  const { mutate: setSavedTitle } = useSavedTitle();
  const { openPicker } = useAddToCanvasPicker();
  const { openVideo, setNavigationList, videoId: openVideoId } = useVideoDetail();
  const [selected, setSelected] = useState<Set<number>>(() => new Set());

  const ids = useMemo(() => videos.map((v) => v.id), [videos]);
  useEffect(() => {
    setNavigationList(ids);
  }, [ids, setNavigationList]);

  // Снятая закладка выпадает из выбора.
  useEffect(() => {
    setSelected((prev) => {
      const next = new Set([...prev].filter((id) => ids.includes(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [ids]);

  const toggle = (id: number) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const clear = () => setSelected(new Set());
  const selectedIds = [...selected];

  return (
    <>
      <div className={styles.head}>
        <div>
          <p className={styles.eyebrow}>{t('library.eyebrow')}</p>
          <h1 className={styles.title}>{t('library.savedTitle')}</h1>
          <p className={styles.lead}>{t('library.savedLead')}</p>
        </div>
        {selectedIds.length > 0 ? (
          <div className={styles.headActions}>
            <span className={styles.selCount}>
              {t('catalog.selectedCount', { n: selectedIds.length })}
            </span>
            <button
              type="button"
              className={styles.primary}
              onClick={() => {
                openPicker(selectedIds, 'saved');
                clear();
              }}
            >
              {t('library.addToCanvasArrow')}
            </button>
            <button
              type="button"
              className={styles.ghost}
              onClick={() => {
                setMany(selectedIds, false);
                clear();
              }}
            >
              {t('library.remove')}
            </button>
            <button type="button" className={styles.quiet} onClick={clear}>
              {t('library.clear')}
            </button>
          </div>
        ) : videos.length > 0 ? (
          <button type="button" className={styles.ghost} onClick={() => setSelected(new Set(ids))}>
            {t('library.selectAll')}
          </button>
        ) : null}
      </div>

      {videos.length > 0 ? (
        <SavedGrid openVideoId={openVideoId}>
          {videos.map((video, index) => (
            <SavedCard
              key={video.id}
              video={video}
              canvasList={canvasList}
              selected={selected.has(video.id)}
              onToggle={() => toggle(video.id)}
              onOpen={() => openVideo({ id: video.id, source: 'saved', position: index })}
              onToCanvas={() => openPicker([video.id], 'saved')}
              onRename={(draft) => {
                const title = savedTitlePatch(draft, video);
                if (title !== undefined) setSavedTitle({ videoId: video.id, title });
              }}
            />
          ))}
        </SavedGrid>
      ) : isLoading ? null : (
        <div className={styles.empty}>
          <h3 className={styles.emptyTitle}>{t('library.nothingSaved')}</h3>
          <p className={styles.emptyText}>
            {t('library.pressPlus')}
            <code>+</code>
            {t('library.pressPlusRest')}
          </p>
          <button type="button" className={styles.emptyButton} onClick={() => navigate('/')}>
            {t('library.goToCatalog')}
          </button>
        </div>
      )}
      {/* На телефоне действия с выбранным плавают над навигацией — нижний ряд не уходит под них. */}
      {selectedIds.length > 0 ? <div className={styles.selectionRoom} aria-hidden="true" /> : null}
    </>
  );
}

/**
 * Сетка Saved: колонки считает CSS (`.savedGrid`), а когда панель разбора сужает
 * страницу и сетка перекладывается — карточка открытого видео остаётся на месте.
 */
function SavedGrid({ openVideoId, children }: { openVideoId: number | null; children: ReactNode }) {
  const gridRef = useRef<HTMLDivElement>(null);
  const keepCardInPlace = useReflowAnchor(gridRef, openVideoId);
  useResizeWidth(gridRef, keepCardInPlace);

  return (
    <div ref={gridRef} className={styles.savedGrid}>
      {children}
    </div>
  );
}

function SavedCard({
  video,
  selected,
  onToggle,
  onOpen,
  onToCanvas,
  onRename,
  canvasList,
}: {
  video: VideoReference;
  canvasList: CanvasSummary[];
  selected: boolean;
  onToggle: () => void;
  onOpen: () => void;
  onToCanvas: () => void;
  /** Своё название закладки из инлайн-поля; что отправлять, решает `savedTitlePatch`. */
  onRename: (draft: string) => void;
}) {
  const canvases = canvasCountOf(video, canvasList);
  const { t, tp } = useTranslator();
  const shown = savedDisplayTitle(video);
  const [renaming, setRenaming] = useState(false);
  const [draft, setDraft] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  // Enter, Esc и потеря фокуса приходят друг за другом — правка завершается один раз.
  const done = useRef(false);

  const startRename = () => {
    done.current = false;
    setDraft(shown);
    setRenaming(true);
    requestAnimationFrame(() => {
      inputRef.current?.focus();
      inputRef.current?.select();
    });
  };
  const finishRename = (commit: boolean) => {
    if (done.current) return;
    done.current = true;
    setRenaming(false);
    if (commit) onRename(draft);
  };
  const onInputKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') finishRename(true);
    if (e.key === 'Escape') {
      // Esc закрывает только поле, а не панель разбора или другой слой.
      e.stopPropagation();
      finishRename(false);
    }
  };
  return (
    <article
      className={cx(styles.savedCard, selected && styles.savedCardSelected)}
      data-video-id={video.id}
    >
      <button type="button" className={styles.savedMedia} onClick={onOpen} aria-label={shown}>
        {video.extra?.thumbnail_url ? (
          <img src={video.extra.thumbnail_url} alt="" loading="lazy" />
        ) : null}
        {video.duration_sec ? (
          <span className={styles.duration}>{formatDuration(video.duration_sec)}</span>
        ) : null}
      </button>
      <button
        type="button"
        role="checkbox"
        aria-checked={selected}
        aria-label={t('library.selectVideo', { title: video.title })}
        title={t('library.select')}
        className={cx(styles.check, selected && styles.checkOn)}
        onClick={onToggle}
      >
        {selected ? <span className={styles.checkMark} aria-hidden="true" /> : null}
      </button>
      <div className={styles.savedFoot}>
        <div className={styles.savedInfo}>
          {renaming ? (
            <input
              ref={inputRef}
              className={cx(styles.renameInput, styles.savedRenameInput)}
              value={draft}
              maxLength={SAVED_TITLE_MAX}
              onChange={(e) => setDraft(e.target.value)}
              onBlur={() => finishRename(true)}
              onKeyDown={onInputKey}
              aria-label={t('library.savedName')}
              data-clarity-mask="true"
            />
          ) : (
            <div className={styles.savedTitleRow}>
              <div
                className={styles.savedTitle}
                title={
                  video.saved_title
                    ? t('library.originalTitle', { title: video.title })
                    : video.title
                }
                data-clarity-mask={video.saved_title ? 'true' : undefined}
              >
                {shown}
              </div>
              <button
                type="button"
                className={styles.renameSaved}
                onClick={startRename}
                title={t('library.rename')}
                aria-label={t('library.renameVideo', { title: shown })}
              >
                <span aria-hidden="true">✎</span>
              </button>
            </div>
          )}
          <div className={styles.savedMeta}>
            {canvases ? tp('library.onCanvasCount', canvases) : t('library.notOnCanvas')}
          </div>
        </div>
        <button
          type="button"
          className={styles.toCanvas}
          title={t('catalog.addToCanvas')}
          onClick={onToCanvas}
        >
          {t('library.toCanvas')}
        </button>
      </div>
    </article>
  );
}
